// RITA-1547 — Entwurfszeile in der Karte „Vertragserstellung“ (Bildentwurf 3b, Bild D, freigegeben von Julia
// am 20.10.2026): unter „Passend für …“, durch eine feine Linie abgetrennt; links „Entwurf vom … · Name“ und
// darunter die enthaltenen Dokumente, rechts in derselben Höhe „Weiterbearbeiten“ und „Verwerfen“.
// „Weiterbearbeiten“ ist wie „Vertrag erstellen“ bei ungespeicherten Änderungen der Beratermaske gesperrt;
// „Verwerfen“ bleibt möglich und fragt vorher nach. Verworfen wird genau der angezeigte Stand (Revision): Hat
// inzwischen jemand anderes gespeichert oder neu begonnen (CONFLICT), zeigt die Rückfrage den neuen Stand.
//
// RITA-1552 (Skizze Rücklauf, Bild E; F8): Dieselbe Zeile gibt es für den eigenen Entwurf der Rücksendung
// („Entwurf Rücksendung vom … · Name“, darunter die Vorlage ohne „– noch nicht gedruckt“ wie 3c-F13). Sie verwirft über
// die eigene Mutation des Vorgangs (ENTWURFSDOKUMENTE) und trägt die Kennungen des Vertrags mit „-ruecksendung“; die
// Kennungen der Vertragszeile bleiben unverändert. Die Rückfrage „Entwurf verwerfen?“ ist wortgleich mit 3b.
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { useMutation } from '@apollo/client';
import { graphqlFehlerCode } from '../../templates/services/vertragsvorlageFehler';
import { ENTWURFSDOKUMENTE, EntwurfVerwerfenVariablen, Vertragsentwurf } from '../api/vertragsentwurf';
import {
  entwurfZeileInhalt,
  entwurfZeileTitel,
  VERWERFEN,
  VERWERFEN_FEHLER,
  WEITERBEARBEITEN,
} from '../services/vertragsentwurfTexte';
import type { Vorgang } from '../services/vorgang';
import { EntwurfVerwerfenDialog } from './EntwurfDialoge';

interface Props {
  agentId: number;
  entwurf: Vertragsentwurf;
  /** RITA-1552: Entwurf „Vertrag erstellen“ (Vorgabe) oder „Rücksendung drucken“. */
  vorgang?: Vorgang;
  /** Beratermaske hat ungespeicherte Änderungen. */
  gesperrt: boolean;
  onWeiterbearbeiten: () => void;
  /** Der angezeigte Stand ist verworfen; die Karte blendet ihn sofort aus, auch wenn das Neuladen scheitert. */
  onVerworfen: () => void;
  /** Nach dem Verwerfen oder bei einem neuen Stand: Entwurf neu abfragen. */
  onNeuLaden: () => Promise<unknown>;
}

function EntwurfZeile({
  agentId,
  entwurf,
  vorgang = 'contract',
  gesperrt,
  onWeiterbearbeiten,
  onVerworfen,
  onNeuLaden,
}: Props) {
  const [verwerfen] = useMutation<{ discardContractCreationDraft: boolean }, EntwurfVerwerfenVariablen>(
    ENTWURFSDOKUMENTE[vorgang].verwerfen,
  );
  /** Kennung für Tests: beim Vertrag wie bisher, bei der Rücksendung mit „-ruecksendung“. */
  const kennung = (basis: string) => (vorgang === 'return' ? `${basis}-ruecksendung` : basis);
  const [rueckfrage, setRueckfrage] = useState(false);
  const [neuerStand, setNeuerStand] = useState(false);
  const [verwirft, setVerwirft] = useState(false);
  const [fehler, setFehler] = useState('');
  const aktiv = useRef(true);
  useEffect(
    () => () => {
      aktiv.current = false;
    },
    [],
  );

  const schliessen = () => {
    setRueckfrage(false);
    setNeuerStand(false);
    setFehler('');
  };

  const bestaetigen = async () => {
    setVerwirft(true);
    setFehler('');
    try {
      await verwerfen({ variables: { agent_id: agentId, expected_revision: entwurf.revision } });
      // Erst schließen, dann ausblenden und neu laden: Scheitert das Neuladen, zeigt die Karte den Ladefehler
      // statt des verworfenen Stands (KI-Review !779).
      if (aktiv.current) {
        schliessen();
        setVerwirft(false);
      }
      onVerworfen();
      onNeuLaden().catch(() => undefined);
    } catch (error) {
      if (graphqlFehlerCode(error) === 'CONFLICT') {
        // Die Karte lädt den neuen Stand; die Rückfrage bleibt offen und nennt ihn.
        await onNeuLaden().catch(() => undefined);
        if (aktiv.current) setNeuerStand(true);
      } else if (aktiv.current) {
        setFehler(VERWERFEN_FEHLER);
      }
      if (aktiv.current) setVerwirft(false);
    }
  };

  return (
    <Box
      data-testid={kennung('vertragsentwurf-zeile')}
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        mt: 2,
        pt: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
        <Typography
          sx={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 0.75 }}
          data-testid={kennung('vertragsentwurf-titel')}
        >
          <EditNoteOutlinedIcon fontSize="small" aria-hidden />
          {entwurfZeileTitel(entwurf.updated_at, entwurf.updated_by_name, vorgang)}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }} data-testid={kennung('vertragsentwurf-inhalt')}>
          {entwurfZeileInhalt(entwurf.documents.map((dokument) => dokument.template_name ?? ''))}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<EditOutlinedIcon />}
          disabled={gesperrt}
          onClick={onWeiterbearbeiten}
          data-testid={kennung('vertragsentwurf-weiterbearbeiten')}
          sx={{ textTransform: 'none' }}
        >
          {WEITERBEARBEITEN}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => setRueckfrage(true)}
          data-testid={kennung('vertragsentwurf-verwerfen')}
          sx={{ textTransform: 'none' }}
        >
          {VERWERFEN}
        </Button>
      </Box>
      {fehler && !rueckfrage && (
        <Alert severity="error" sx={{ width: '100%' }}>
          {fehler}
        </Alert>
      )}
      {rueckfrage && (
        <EntwurfVerwerfenDialog
          entwurf={entwurf}
          neuerStand={neuerStand}
          verwirft={verwirft}
          fehler={fehler}
          onAbbrechen={schliessen}
          onVerwerfen={() => {
            bestaetigen().catch(() => undefined);
          }}
        />
      )}
    </Box>
  );
}

export default EntwurfZeile;
